import useTranslation from 'next-translate/useTranslation';

//type
import type { FormatDateType } from '@/components/pages/settings/subscription-invoices/PageContent/Components/RightSection';

// Hooks
import useToggle from '@/utils/hooks/utils/useToggle';
import useFormatNumber from '@/hooks/format/useFormatNumber';
import useUser from '@/utils/hooks/swr/useUser';
import useReceipts from '@/utils/hooks/swr/pricing/useReceipts';

// Components
import Button from '@/components/base/Button';
import InvoicesListModal from '@/components/pages/settings/subscription-invoices/PageContent/Components/InvoicesListModal';
import Icon from '@/components/base/Icon';

type ReceiptsProps = {
    formatDate: FormatDateType;
};

export function Receipts({ formatDate }: ReceiptsProps) {
    const { t } = useTranslation('settings');
    const { isOpen, toggle } = useToggle();
    const { receipts, loadMoreReceipts, canLoadMore, isLoadingMore } = useReceipts();
    const { user } = useUser({
        revalidateIfStale: false,
        revalidateOnFocus: false,
        revalidateOnReconnect: false
    });

    const userNumberFormat = user?.settings?.numbers_format;
    const { formatPriceCurrencySymbol } = useFormatNumber(userNumberFormat);

    if (!receipts?.length) {
        return (
            <p data-testid="no-receipts" className="text-sm">
                {t('subscription_invoices_page.no_invoices')}
            </p>
        );
    }

    const displayedReceipts = receipts.slice(0, 3);

    return (
        <>
            <p
                className="mb-3 text-xs text-icoGray-500 dark:text-icoDarkMode-wolf"
                data-testid="receipts-section">
                {t('subscription_invoices_page.last_invoices')}
            </p>
            {displayedReceipts.map((receipt) => (
                <p key={receipt.id} className="mt-3 flex items-center gap-2">
                    <span className="flex-1 text-sm">{formatDate(receipt?.created)}</span>
                    <a
                        href={receipt?.url}
                        className="group flex items-center gap-2 text-sm text-icoBlue"
                        target="_blank"
                        rel="noopener noreferrer">
                        <span>
                            {formatPriceCurrencySymbol(Number(receipt.total), receipt?.currency)}
                        </span>
                        <Icon icon="chevron-right transition group-hover:translate-x-1" />
                    </a>
                </p>
            ))}

            <Button
                className="m-auto mb-2 mt-8 w-full"
                variant="secondary"
                onClick={toggle}
                data-testid="button-toggle-invoice-modal">
                {t('subscription_invoices_page.show_all_invoices')}
            </Button>

            <InvoicesListModal
                isOpen={isOpen}
                toggle={toggle}
                invoices={receipts}
                formatDate={formatDate}
                canLoadMore={canLoadMore}
                isLoadingMore={isLoadingMore}
                loadMoreReceipts={loadMoreReceipts}
            />
        </>
    );
}
